import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowRight, Users, Calendar, Clock, ArrowLeft, ShieldCheck } from "lucide-react";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("hr-HR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Zagreb",
    }).format(d);
  } catch {
    return iso;
  }
}

function todayLocalISO() {
  const d = new Date();
  // Get YYYY-MM-DD in local Europe/Zagreb day terms
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zagreb" }).format(d);
}

function getSessionId() {
  let s = localStorage.getItem("kc_booking_sid");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("kc_booking_sid", s);
  }
  return s;
}

function CountdownPill({ until }) {
  const [left, setLeft] = useState(Math.max(0, Math.floor((new Date(until) - new Date()) / 1000)));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, Math.floor((new Date(until) - new Date()) / 1000))), 1000);
    return () => clearInterval(id);
  }, [until]);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-primary/40 text-primary" data-testid="booking-hold-timer">
      <Clock className="w-3 h-3" /> {mm}:{ss}
    </span>
  );
}

export default function BookingPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const eventSlug = params.get("event");

  const [config, setConfig] = useState(null);
  const [event, setEvent] = useState(null);
  const [date, setDate] = useState(todayLocalISO());
  const [party, setParty] = useState(2);
  const [availability, setAvailability] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {slot, group}
  const [step, setStep] = useState(0); // 0 search results, 1 customer info, 2 payment choice, 3 confirm
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", notes: "" });
  const [paymentChoice, setPaymentChoice] = useState("reservation");
  const [hold, setHold] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/public/booking/config").then((r) => setConfig(r.data));
    if (eventSlug) {
      api.get(`/public/events/${eventSlug}`).then((r) => setEvent(r.data)).catch(() => {});
    }
  }, [eventSlug]);

  // When event is loaded, lock date to its day
  useEffect(() => {
    if (event?.starts_at) {
      const iso = new Date(event.starts_at).toLocaleDateString("en-CA", { timeZone: "Europe/Zagreb" });
      setDate(iso);
    }
  }, [event]);

  // When config changes & both modes allowed → default to reservation, but the choice screen will show.
  useEffect(() => {
    if (!config) return;
    const p = config.pricing;
    if (p.pay_now_enabled && !p.reservation_only_enabled) setPaymentChoice("pay_now");
    else setPaymentChoice("reservation");
  }, [config]);

  const search = async () => {
    setSearching(true);
    setAvailability(null);
    try {
      const { data } = await api.post("/public/booking/availability", {
        date,
        party_size: party,
        event_id: event?.id || undefined,
      });
      setAvailability(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSearching(false);
    }
  };

  const openReserve = async (slot, group) => {
    setSelected({ slot, group });
    setStep(1);
    setCustomer({ name: "", email: "", phone: "", notes: "" });
    // place hold
    try {
      const { data } = await api.post("/public/booking/hold", {
        table_ids: group.table_ids,
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        session_id: getSessionId(),
      });
      setHold(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setStep(0);
      setSelected(null);
    }
  };

  const closeDialog = () => {
    setSelected(null);
    setHold(null);
    setStep(0);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/booking/create", {
        hold_id: hold.hold_id,
        customer: { ...customer, locale },
        party_size: party,
        payment_choice: paymentChoice,
        event_id: event?.id || undefined,
        origin_url: window.location.origin,
      });
      if (data.checkout_url && paymentChoice === "pay_now") {
        window.location.href = data.checkout_url;
        return;
      }
      navigate(`/booking/confirm?code=${data.booking.code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showPaymentStep = config?.pricing?.allow_user_choice && config?.pricing?.pay_now_enabled && config?.pricing?.reservation_only_enabled;
  const maxParty = config?.slots?.max_party_size || 12;
  const totalAmount = availability?.pricing?.total_amount || 0;

  return (
    <PublicLayout pageTitle={t("booking_page.title")} testid="booking-page">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        {event && (
          <button onClick={() => navigate(`/events/${event.slug}`)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="booking-back-to-event">
            <ArrowLeft className="w-3 h-3" /> {pickLocalized(event.title, locale)}
          </button>
        )}
        <p className="text-xs uppercase tracking-[0.3em] text-primary/80 mt-3">— {t("nav.booking")}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3" data-testid="booking-title">
          {event ? pickLocalized(event.title, locale) : t("booking_page.title")}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          {event ? t("nav.events") : "Odaberite datum i broj osoba — pronaći ćemo idealan stol."}
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="border border-border rounded-2xl bg-card p-5 sm:p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end" data-testid="booking-search-card">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-primary" /> Datum / Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date-input" disabled={!!event} />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-primary" /> Osoba / Party</Label>
            <Input type="number" min={1} max={maxParty} value={party} onChange={(e) => setParty(Math.max(1, Math.min(maxParty, parseInt(e.target.value || "1"))))} data-testid="booking-party-input" />
          </div>
          <Button onClick={search} disabled={searching} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="booking-search-button">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Traži / Find <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>

        {availability && availability.slots.length === 0 && (
          <div className="mt-6 text-sm text-muted-foreground text-center py-10 border border-dashed border-border rounded-xl" data-testid="booking-no-slots">
            Nema slobodnih termina za odabrani datum/broj osoba.
          </div>
        )}

        {availability && availability.slots.length > 0 && (
          <div className="mt-6 grid gap-3" data-testid="booking-slots-grid">
            {availability.slots.map((slot, idx) => (
              <div key={idx} className="border border-border rounded-xl bg-card p-4" data-testid={`booking-slot-${idx}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-primary" data-testid={`booking-slot-time-${idx}`}>{formatTime(slot.starts_at)}</span>
                    <span className="text-xs text-muted-foreground">— {formatTime(slot.ends_at)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {availability.pricing.mode === "reservation_only"
                      ? "Rezervacija besplatna"
                      : `Ukupno € ${Number(totalAmount).toFixed(2)}`}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`booking-group-${idx}`}>
                  {slot.available_table_groups.map((g, gi) => (
                    <button
                      type="button"
                      key={gi}
                      onClick={() => openReserve(slot, g)}
                      className="px-3 py-2 rounded-full border border-border hover:border-primary/40 hover:text-primary text-sm transition"
                      data-testid={`booking-pick-${idx}-${gi}`}
                    >
                      {g.labels.join(" + ")} · {g.total_seats} sjedala
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg" data-testid="booking-reserve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Rezervacija / Reservation</span>
              {hold && <CountdownPill until={hold.expires_at} />}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                {formatTime(selected.slot.starts_at)} — {formatTime(selected.slot.ends_at)} · {selected.group.labels.join(" + ")} · {party} osoba
              </div>
              {step === 1 && (
                <div className="space-y-3" data-testid="booking-customer-step">
                  <div className="space-y-2">
                    <Label>Ime i prezime / Full name</Label>
                    <Input required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="booking-customer-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" required value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="booking-customer-email" />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon / Phone</Label>
                      <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="booking-customer-phone" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Napomena / Note (opt.)</Label>
                    <Textarea rows={2} value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} data-testid="booking-customer-notes" />
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={() => (showPaymentStep ? setStep(2) : setStep(3))}
                      disabled={!customer.name || !customer.email}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="booking-customer-continue"
                    >
                      Dalje / Continue <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </DialogFooter>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-3" data-testid="booking-payment-step">
                  <label className="flex items-start gap-3 border border-border rounded-lg p-3 cursor-pointer">
                    <input type="radio" name="pay" checked={paymentChoice === "reservation"} onChange={() => setPaymentChoice("reservation")} data-testid="booking-pay-reservation" />
                    <span className="text-sm">Rezervacija bez plaćanja</span>
                  </label>
                  <label className="flex items-start gap-3 border border-border rounded-lg p-3 cursor-pointer">
                    <input type="radio" name="pay" checked={paymentChoice === "pay_now"} onChange={() => setPaymentChoice("pay_now")} data-testid="booking-pay-now" />
                    <span className="text-sm">Plati odmah (Stripe) — € {Number(totalAmount).toFixed(2)}</span>
                  </label>
                  <DialogFooter>
                    <Button onClick={() => setStep(3)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="booking-payment-continue">
                      Dalje <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </DialogFooter>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-3" data-testid="booking-confirm-step">
                  <div className="text-sm">
                    <div><b>{customer.name}</b> · {customer.email}</div>
                    <div className="text-muted-foreground">{customer.phone || "—"}</div>
                  </div>
                  <div className="text-sm border-t border-border pt-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    {paymentChoice === "pay_now"
                      ? `Naplaćuje se € ${Number(totalAmount).toFixed(2)} preko Stripea.`
                      : "Rezervacija bez naplate. Potvrda stiže emailom."}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setStep(showPaymentStep ? 2 : 1)} className="border-border">Natrag</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="booking-confirm-button">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Potvrdi / Confirm"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 mt-10 mb-16 text-xs text-muted-foreground">
        {config && !config.stripe_enabled && (
          <p data-testid="booking-stripe-disabled">Online plaćanje trenutno nije omogućeno — rezervacija bez plaćanja.</p>
        )}
      </section>
    </PublicLayout>
  );
}
